import type { Lang } from '@/hooks/useLang'

interface ProductPreviewProps {
  lang: Lang
}

// TODO: remplacer les placeholders CSS par les images WebP dans apps/web/public/images/robots/
// Voir docs/assets-todo.md — section 4

const PRODUCTS = {
  fr: [
    {
      id: 'atlas',
      badge: 'LOGISTICS PRO',
      badgeColor: 'text-primary-container',
      title: 'Châssis Atlas-7',
      desc: "Optimisé pour la force et l'endurance en milieu hostile.",
      barColor: 'bg-primary-container',
      gradient: 'from-surface-container-high to-primary-container/20',
    },
    {
      id: 'aura',
      badge: 'AURA SERIES',
      badgeColor: 'text-secondary',
      title: 'Liaison Aura',
      desc: "Design fluide et interface émotionnelle pour l'interaction humaine.",
      barColor: 'bg-secondary',
      gradient: 'from-surface-container-high to-secondary/20',
    },
    {
      id: 'omega',
      badge: 'SCIENTIFIC-X',
      badgeColor: 'text-primary',
      title: 'Spec-Omega',
      desc: 'Précision micrométrique pour la recherche et le développement.',
      barColor: 'bg-primary',
      gradient: 'from-surface-container-high to-primary/20',
    },
  ],
  en: [
    {
      id: 'atlas',
      badge: 'LOGISTICS PRO',
      badgeColor: 'text-primary-container',
      title: 'Atlas-7 Chassis',
      desc: 'Optimized for strength and endurance in hostile environments.',
      barColor: 'bg-primary-container',
      gradient: 'from-surface-container-high to-primary-container/20',
    },
    {
      id: 'aura',
      badge: 'AURA SERIES',
      badgeColor: 'text-secondary',
      title: 'Aura Link',
      desc: 'Fluid design and emotional interface for human interaction.',
      barColor: 'bg-secondary',
      gradient: 'from-surface-container-high to-secondary/20',
    },
    {
      id: 'omega',
      badge: 'SCIENTIFIC-X',
      badgeColor: 'text-primary',
      title: 'Spec-Omega',
      desc: 'Micrometric precision for research and development.',
      barColor: 'bg-primary',
      gradient: 'from-surface-container-high to-primary/20',
    },
  ],
}

const COPY = {
  fr: {
    title: 'Configurations Recommandées',
    subtitle: "Explorez nos châssis pré-configurés, optimisés pour différents secteurs d'activité, de la logistique à l'assistance personnelle.",
    cta: 'Explorer la Galerie',
  },
  en: {
    title: 'Recommended Configurations',
    subtitle: 'Explore our pre-configured chassis, optimized for different sectors, from logistics to personal assistance.',
    cta: 'Explore the Gallery',
  },
}

export default function ProductPreview({ lang }: ProductPreviewProps) {
  const t = COPY[lang]
  const products = PRODUCTS[lang]

  return (
    <section id="preview" className="py-24 px-8 md:px-16">
      {/* Header ligne */}
      <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
        <div className="max-w-xl">
          <h2 className="font-headline text-4xl md:text-5xl font-bold mb-6 text-on-surface">
            {t.title}
          </h2>
          <p className="text-on-surface-variant font-body leading-relaxed">{t.subtitle}</p>
        </div>
        <div className="h-px flex-grow bg-outline-variant/10 mx-8 hidden md:block" />
        <button className="flex items-center gap-2 label-technical text-primary hover:text-secondary transition-colors whitespace-nowrap">
          {t.cta}
          <span className="material-symbols-outlined text-base">arrow_forward</span>
        </button>
      </div>

      {/* Grille produits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
        {products.map(product => (
          <article key={product.id} className="group cursor-pointer">
            {/* Image placeholder */}
            <div className="relative aspect-[4/5] overflow-hidden rounded-lg mb-6 bg-surface-container-high/50">
              {/* Gradient placeholder — à remplacer par <img> quand les assets sont disponibles */}
              <div
                className={`w-full h-full bg-gradient-to-br ${product.gradient} opacity-60 group-hover:opacity-90 group-hover:scale-110 transition-all duration-700`}
              >
                {/* Déco intérieure placeholder */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="material-symbols-outlined text-7xl text-on-surface-variant/20 group-hover:scale-110 transition-transform duration-700">
                    smart_toy
                  </span>
                </div>
              </div>

              {/* Badge */}
              <div className={`absolute top-4 left-4 bg-slate-900/80 backdrop-blur px-3 py-1 rounded-full font-body text-[9px] tracking-widest ${product.badgeColor}`}>
                {product.badge}
              </div>
            </div>

            {/* Infos */}
            <h4 className="font-headline text-xl mb-2 text-on-surface">{product.title}</h4>
            <p className="text-on-surface-variant text-sm mb-4 font-body">{product.desc}</p>

            {/* Barre de progression hover */}
            <div className="h-0.5 w-full bg-surface-container-highest rounded-full overflow-hidden">
              <div className={`h-full ${product.barColor} w-0 group-hover:w-full transition-all duration-500`} />
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}
